import React from 'react';
import { styles } from './InstallInfoBlock.css';
import { Icon, IconNameEnum } from '@jbhi-fi/lanyard-ui/components/Icon';
import { PossiblyResponsiveValue, useResponsiveValue } from '../../hooks/useResponsiveValue';
import { OfferingsProps } from '@jbhi-fi/apis/src/services/types';
import Checkbox from '@jbhi-fi/lanyard-ui/components/Checkbox';
import CalloutFont from '@jbhi-fi/lanyard-ui/font/CalloutFont';
import { useIdPrefix } from '../../IdPrefix';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { formatMoney } from '@jbhi-fi/lanyard-ui/global/utils';
import { InstallOffer } from './InstallOffer';
import { Message } from '@jbhi-fi/components/src/PdpProductProvider/types';

const CABLE_HIDING_EXPLANATION_LINK = '/pages/help-and-support/cable-hiding-explained';

export type InstallInfoBlockProps = {
  heading: string;
  headingLogoURL?: string | null;
  headingLogoName?: IconNameEnum | keyof typeof IconNameEnum;
  headingLogoSize?: PossiblyResponsiveValue<{ width: number; height: number }>;
  headingFontSize?: 'normal' | 'larger';
  subText?: string;
  variant?: 'checkbox' | 'button';
  selectedOfferingId?: string;
  features?: Array<string>;
  offerings: Array<OfferingsProps>;
  onAdd: (event: React.ChangeEvent | React.SyntheticEvent, offeringId: string) => void;
  onRemove: (offeringId?: string) => void;
  onTrack: (trackingData: { linkText: string; linkUrl: string }) => void;
  buttonPosition?: 'bottom' | 'right';
  isCartReady?: boolean;
  isSlideout?: boolean;
  installationMessages?: Array<Message>;
};

const Wrapper = ({
  heading,
  headingLogoName,
  headingLogoURL,
  headingLogoSize,
  headingFontSize,
  subText,
  testIdPrefix,
  children,
}: Pick<InstallInfoBlockProps, 'heading' | 'headingLogoName' | 'subText' | 'headingLogoURL' | 'headingFontSize'> & {
  children: React.ReactNode;
  headingLogoSize: PossiblyResponsiveValue<{ width: number; height: number }>;
  testIdPrefix?: string;
}) => {
  const { height: logoHeight, width: logoWidth } = useResponsiveValue(headingLogoSize);
  const isMobile = useBreakpoint('md', 'down');

  return (
    <div className={styles.wrapperContainer} data-testid={`${testIdPrefix}service-block`}>
      <div className={styles.headingContainer} data-testid={`${testIdPrefix}service-heading-container`}>
        {headingLogoURL ? (
          <img
            src={headingLogoURL}
            alt="headingLogo"
            data-testid={`${testIdPrefix}service-icon`}
            className={styles.headingIcon}
            height={logoHeight}
            width={logoWidth}
          />
        ) : headingLogoName ? (
          <Icon
            name={headingLogoName}
            data-testid={`${testIdPrefix}service-icon`}
            className={styles.headingIcon}
            height={logoHeight}
            width={logoWidth}
          />
        ) : null}
        <CalloutFont
          className={styles.heading}
          as={headingFontSize === 'larger' && !isMobile ? 'h2' : 'h3'}
          testId={`${testIdPrefix}service-heading`}
        >
          {heading}
        </CalloutFont>
      </div>
      {subText ? (
        <p className={styles.subText} data-testid={`${testIdPrefix}service-sub-text`}>
          {subText}
        </p>
      ) : null}
      {children}
    </div>
  );
};

export const InstallInfoBlock = ({
  heading,
  headingLogoSize = { width: 30, height: 30 },
  headingLogoURL,
  subText,
  selectedOfferingId,
  onAdd,
  onRemove,
  onTrack,
  isCartReady,
  offerings,
  installationMessages,
}: InstallInfoBlockProps) => {
  const testIdPrefix = `${useIdPrefix()}install-`;

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    offering: OfferingsProps & {
      heading?: string;
      subText?: string;
      features?: Array<string>;
    }
  ) => {
    const isCurrentlySelected = selectedOfferingId === offering.id;

    if (isCurrentlySelected) {
      onRemove(offering.id);
    } else {
      onAdd(event, offering.id);
    }
  };

  return (
    <Wrapper
      heading={heading}
      headingLogoURL={headingLogoURL}
      headingLogoSize={headingLogoSize}
      headingFontSize="normal"
      subText={subText}
      testIdPrefix={testIdPrefix}
    >
      {!!installationMessages?.length &&
        installationMessages.map(message => (
          <InstallOffer
            key={message.id}
            title={message.heading}
            shortDescription={message.shortDescription}
            description={message.description}
          />
        ))}
      <div className={styles.offeringContainer}>
        {offerings?.map(offering => {
          const isSelected = selectedOfferingId === offering.id;
          return (
            <div
              key={offering.id}
              className={styles.checkboxContainer}
              data-testid={`${testIdPrefix}checkbox-container`}
            >
              <div className={styles.checkboxWrapper}>
                {!isCartReady && isSelected ? (
                  <Icon name="Loading" height={27} width={27} />
                ) : (
                  <Checkbox
                    name="add-service"
                    label=""
                    disabled={!isCartReady}
                    onChange={e => handleChange(e, offering)}
                    checked={isSelected}
                    className="InstallInfoBlock__checkbox"
                    data-testid={`${testIdPrefix}checkbox`}
                    id={`service-checkbox-${offering.id}`}
                  />
                )}
              </div>

              <div className={styles.contentContainer} data-testid={`${testIdPrefix}details`}>
                <button
                  type="button"
                  onClick={e => onAdd(e, offering.id)}
                  className={styles.contentHeading}
                  data-testid={`${testIdPrefix}heading`}
                >
                  {offering.heading ?? offering.name ?? offering.shortName}
                </button>

                {offering.subText ? (
                  <p className={styles.contentSubText} data-testid={`${testIdPrefix}sub-text`}>
                    {offering.subText}{' '}
                    {offering.explained ? (
                      <a
                        className={styles.contentLink}
                        href={CABLE_HIDING_EXPLANATION_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() =>
                          onTrack({
                            linkText: offering.explained ?? '',
                            linkUrl: CABLE_HIDING_EXPLANATION_LINK,
                          })
                        }
                      >
                        {offering.explained}
                      </a>
                    ) : null}
                  </p>
                ) : null}

                {offering?.shortServiceFeatures?.length ? (
                  <ul className={styles.list} data-testid={`${testIdPrefix}list`}>
                    {offering.shortServiceFeatures.map((feature, index) => (
                      <li key={index} className={styles.listItem}>
                        {feature}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
              <div>
                {offering?.priceToShow === 0
                  ? 'FREE'
                  : offering?.priceToShow != null
                  ? formatMoney(offering.priceToShow)
                  : null}
              </div>
            </div>
          );
        })}
      </div>
    </Wrapper>
  );
};
